/**
 * Per-day order-status for the family weekly view — the "Order by / Edit by /
 * Complete / Not ordered" badge model from the Figma design.
 *
 * Each day is resolved against ITS OWN `DeadlineStatus`: under a FIXED_WEEKLY
 * policy every day of the service week shares one cutoff, while RELATIVE_DAYS
 * gives each day a distinct "N days before service" cutoff. Alongside the
 * deadline, only whether THAT day has a placed (and still-editable) order
 * varies. Pure — the badge component renders the result.
 */

import type { DeadlineStatus } from '@/lib/school-orders/deadline'

export type DayOrderStatus = 'ORDER_BY' | 'EDIT_BY' | 'PAYMENT_PENDING' | 'COMPLETE' | 'NOT_ORDERED' | 'NONE'

/** Semantic color tone for the status badge (drives the "Light" badge styling). */
export type DayBadgeTone = 'info' | 'success' | 'warning' | 'neutral'

/**
 * Tone for a status: open actionable states are info (order) / success (edit),
 * but escalate to warning when the deadline is *today*. Closed states are
 * success (ordered) / neutral (missed).
 */
export function dayBadgeTone(status: DayOrderStatus, deadlineToday: boolean): DayBadgeTone {
  switch (status) {
    case 'ORDER_BY':
      return deadlineToday ? 'warning' : 'info'
    case 'EDIT_BY':
      return deadlineToday ? 'warning' : 'success'
    case 'PAYMENT_PENDING':
      return 'warning'
    case 'COMPLETE':
      return 'success'
    case 'NOT_ORDERED':
    case 'NONE':
      return 'neutral'
  }
}

export interface DayStatusInput {
  /** THIS day's deadline status (shared week-wide only under FIXED_WEEKLY). */
  deadline: DeadlineStatus
  /** A submitted order exists for this day in this household. */
  hasOrder: boolean
  /** That order is still editable (window open + nothing served/billed). */
  editable: boolean
  /**
   * A payment-pending order exists (checkout debit failed; automatic retry
   * scheduled). Wins over the other order states — the customer must add
   * funds or cancel the retry.
   */
  paymentPending?: boolean
  /**
   * The day itself is in the past (before today). Past days are never orderable
   * or editable — even when no deadline policy is set — so they always read as
   * closed regardless of the weekly deadline.
   */
  dayIsPast?: boolean
}

/**
 * Resolve a day's badge state:
 *   - past day → closed: COMPLETE if ordered, else NOT_ORDERED
 *   - no deadline policy → ordering never closes (ordered ⇒ EDIT_BY, else no badge)
 *   - window closed → COMPLETE if ordered, else NOT_ORDERED
 *   - window open → EDIT_BY if an editable order exists, ORDER_BY if none
 *     (a placed-but-locked order — served/billed mid-window — reads COMPLETE)
 */
export function resolveDayStatus({
  deadline,
  hasOrder,
  editable,
  paymentPending = false,
  dayIsPast = false,
}: DayStatusInput): DayOrderStatus {
  if (dayIsPast) {
    return hasOrder ? 'COMPLETE' : 'NOT_ORDERED'
  }
  // Payment-pending wins over every open-window state: the order is neither
  // placed nor absent, and the badge must prompt the add-funds/cancel choice.
  if (paymentPending && !deadline.closed) {
    return 'PAYMENT_PENDING'
  }
  if (deadline.deadline === null) {
    return hasOrder ? 'EDIT_BY' : 'NONE'
  }
  if (deadline.closed) {
    return hasOrder ? 'COMPLETE' : 'NOT_ORDERED'
  }
  if (hasOrder) {
    return editable ? 'EDIT_BY' : 'COMPLETE'
  }
  return 'ORDER_BY'
}

/**
 * Human label for a status, formatted against the location's timezone. When
 * the deadline falls TODAY, "Order by Tue" is useless to someone deciding
 * whether to order right now — show the cutoff time instead ("Order by
 * midnight" / "Order by 3:00 PM").
 */
export function formatDayStatusLabel(
  status: DayOrderStatus,
  deadline: Date | null,
  timeZone: string,
  deadlineIsToday = false,
): string {
  switch (status) {
    case 'ORDER_BY':
      return deadline ? `Order by ${formatDeadline(deadline, timeZone, deadlineIsToday)}` : 'Order'
    case 'EDIT_BY':
      return deadline ? `Edit by ${formatDeadline(deadline, timeZone, deadlineIsToday)}` : 'Edit order'
    case 'PAYMENT_PENDING':
      return 'Payment pending'
    case 'COMPLETE':
      return 'Complete'
    case 'NOT_ORDERED':
      return 'Ordering closed'
    case 'NONE':
      return ''
  }
}

export interface DayStatusSummary {
  /** Leading placed/not-placed clause, e.g. "Order placed". */
  lead: string
  /** Remaining-window clause, e.g. "Due in 3 days" — null when there's nothing to add. */
  detail: string | null
}

/**
 * One-line summary for the day view's status row — the fuller-sentence
 * counterpart to `formatDayStatusLabel`'s pill copy: "No order placed · Due
 * in 3 days" / "Order placed · Edits due tomorrow". The row sits right next
 * to the weekday title, so the window renders RELATIVE to `now` instead of
 * naming a second weekday; once the cutoff is under 24 hours away the clause
 * carries the time itself ("Due 3:00 PM", "Due 8:00 AM tomorrow").
 */
export function formatDayStatusSummary(
  status: DayOrderStatus,
  deadline: Date | null,
  timeZone: string,
  now: Date,
): DayStatusSummary | null {
  const due = deadline ? formatDueClause(deadline, timeZone, now) : null
  switch (status) {
    case 'ORDER_BY':
      return { lead: 'No order placed', detail: due ? `Due ${due}` : null }
    case 'EDIT_BY':
      return { lead: 'Order placed', detail: due ? `Edits due ${due}` : null }
    case 'PAYMENT_PENDING':
      // The banner below the row owns the add-funds / cancel actions.
      return { lead: 'Payment pending', detail: null }
    case 'COMPLETE':
      return { lead: 'Order placed', detail: null }
    case 'NOT_ORDERED':
      return { lead: 'No order placed', detail: 'Ordering closed' }
    case 'NONE':
      return null
  }
}

function formatDeadline(d: Date, timeZone: string, deadlineIsToday: boolean): string {
  if (!deadlineIsToday) {
    return new Intl.DateTimeFormat(undefined, { weekday: 'short', timeZone }).format(d)
  }
  return timeOfDayClause(d, timeZone)
}

/** `YYYY-MM-DD` of an instant in the location's timezone. */
function dayKeyInZone(d: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d)
}

/** Calendar days from `now`'s day to `deadline`'s day, in the location's timezone. */
function daysUntilInZone(deadline: Date, now: Date, timeZone: string): number {
  return Math.round(
    (Date.parse(dayKeyInZone(deadline, timeZone)) - Date.parse(dayKeyInZone(now, timeZone))) / 86_400_000,
  )
}

/**
 * Relative window clause for the status row: "in 3 days" / "tomorrow", and
 * once a day count stops helping, the cutoff time itself — "3:00 PM" the day
 * of, "8:00 AM tomorrow" when under 24 hours across midnight.
 */
function formatDueClause(deadline: Date, timeZone: string, now: Date): string {
  const days = daysUntilInZone(deadline, now, timeZone)
  if (days <= 0) {
    return timeOfDayClause(deadline, timeZone)
  }
  if (deadline.getTime() - now.getTime() < 86_400_000) {
    return `${timeOfDayClause(deadline, timeZone)} tomorrow`
  }
  return days === 1 ? 'tomorrow' : `in ${days} days`
}

/** Wall-clock cutoff time — 23:59 (or 00:00) reads better as "midnight" than "11:59 PM". */
function timeOfDayClause(d: Date, timeZone: string): string {
  const hm = new Intl.DateTimeFormat('en-CA', { timeZone, hour: '2-digit', minute: '2-digit', hour12: false }).format(d)
  if (hm === '23:59' || hm === '00:00' || hm === '24:00') {
    return 'midnight'
  }
  return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', timeZone }).format(d)
}
